import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "../utils";

// ── Query parameter types ──────────────────────────────────────────

export interface AgtechTicketsFilters {
  scale_ticket_inbound_outbound?: string;
  customer_ids?: string;
  scale_ticket_id?: string;
  ship_date?: string;
  plc_id?: string;
  commodity_id?: string;
  page?: number;
  page_size?: number;
}

// ── Response types ─────────────────────────────────────────────────

export interface AgtechTicketsPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface AgtechTicketsResponse {
  data: TransformedTicket[];
  pagination: AgtechTicketsPagination;
}

export interface TransformedTicket {
  scale_ticket_id: string;
  entries: TransformedEntry[];
}

export type InstanceType = "settlement" | "advance";

export interface CustomerInfo {
  long_name: string;
}

export interface TransformedEntry {
  quantity_entry_nbr: number;
  scale_ticket_id: string | null;
  scale_ticket_gross_weight: string | null;
  scale_ticket_tare_weight: string | null;
  scale_quantity_lb: string | null;
  vendor_id: string;
  customer_id: string | null;
  Customer: CustomerInfo | null;
  vehicle_id: string | null;
  plc_id: string;
  Location?: { long_name: string } | null;
  commodity_id: string;
  commodity_display_name: string | null;
  reference_code: string | null;
  ship_date: string | null;
  add_date: string | null;
  scale_uom_code: string | null;
  report_uom_code: string | null;
  instance_type: InstanceType | null;
  detailed_info?: {
    serial_nbr: number | string | null;
    ship_from_id: string | null;
    ship_to_id: string | null;
    ShipFrom: { long_name: string } | null;
    ShipTo: { long_name: string } | null;
    grades: { charge_grade_code: string; charge_grade_value: string; }[];
  } | null;
  splits: Ts1ScaleTicketActualSplitRow[];
}

export interface Ts1ScaleTicketActualSplitRow {
  id: string;
  deleted_at: string | null;
  quantity_entry_nbr: number;
  ticket_related_id: string;
  sort_order: number;
  quantity_lb: string | null;
  hold_quantity_lb: string | null;
  unapplied_quantity_lb: string | null;
  applied_quantity_lb: string | null;
  shrink_quantity_lb: string | null;
  applied_quantity: string | null;
  shrink_quantity: string | null;
}

// ── Flattened row type for the DataTable ───────────────────────────

export interface FlattenedTicketRow {
  // Row identity
  _rowKey: string;

  // Scale ticket grouping
  scale_ticket_id: string;

  // Entry fields
  quantity_entry_nbr: number;
  scale_ticket_gross_weight: string | null;
  scale_ticket_tare_weight: string | null;
  scale_quantity_lb: string | null;
  vendor_id: string;
  customer_id: string | null;
  customer_name: string | null;
  vehicle_id: string | null;
  plc_id: string;
  location_name: string | null;
  commodity_id: string;
  commodity_display_name: string | null;
  reference_code: string | null;
  ship_date: string | null;
  add_date: string | null;
  scale_uom_code: string | null;
  report_uom_code: string | null;
  instance_type: InstanceType | null;

  // Split fields (one row per split)
  split_sort_order: number | null;
  split_ticket_related_id: string | null;
  split_quantity_lb: string | null;
  split_hold_quantity_lb: string | null;
  split_unapplied_quantity_lb: string | null;
  split_applied_quantity_lb: string | null;
  split_shrink_quantity_lb: string | null;

  split_applied_quantity?: string | null;
  split_shrink_quantity?: string | null;

  serial_nbr?: number | string | null;
  ship_from?: string | null;
  ship_to?: string | null;

  // Grades
  grades: { charge_grade_code: string; charge_grade_value: string; }[];
}

// ── Flatten helper ─────────────────────────────────────────────────

export function flattenTickets(tickets: TransformedTicket[]): FlattenedTicketRow[] {
  const rows: FlattenedTicketRow[] = [];

  for (const ticket of tickets) {
    for (const entry of ticket.entries) {
      const baseRow = {
        scale_ticket_id: ticket.scale_ticket_id,
        quantity_entry_nbr: entry.quantity_entry_nbr,
        scale_ticket_gross_weight: entry.scale_ticket_gross_weight,
        scale_ticket_tare_weight: entry.scale_ticket_tare_weight,
        scale_quantity_lb: entry.scale_quantity_lb,
        vendor_id: entry.vendor_id,
        customer_id: entry.customer_id,
        customer_name: entry.Customer?.long_name ?? null,
        vehicle_id: entry.vehicle_id,
        plc_id: entry.plc_id,
        location_name: entry.Location?.long_name ?? null,
        commodity_id: entry.commodity_id,
        commodity_display_name: entry.commodity_display_name,
        reference_code: entry.reference_code,
        ship_date: entry.ship_date,
        add_date: entry.add_date,
        scale_uom_code: entry.scale_uom_code,
        report_uom_code: entry.report_uom_code,
        instance_type: entry.instance_type,
        serial_nbr: entry.detailed_info?.serial_nbr,
        ship_from: entry.detailed_info?.ShipFrom?.long_name,
        ship_to: entry.detailed_info?.ShipTo?.long_name,
        grades: entry.detailed_info?.grades ?? [],
      };

      if (entry.splits.length === 0) {
        rows.push({
          ...baseRow,
          _rowKey: `${entry.quantity_entry_nbr}-no-split`,
          split_sort_order: null,
          split_ticket_related_id: null,
          split_quantity_lb: null,
          split_hold_quantity_lb: null,
          split_unapplied_quantity_lb: null,
          split_applied_quantity_lb: null,
          split_shrink_quantity_lb: null,
        });
      } else {
        for (const split of entry.splits) {
          rows.push({
            ...baseRow,
            _rowKey: `${entry.quantity_entry_nbr}-split-${split.id}`,
            split_sort_order: split.sort_order,
            split_ticket_related_id: split.ticket_related_id,
            split_quantity_lb: split.quantity_lb,
            split_hold_quantity_lb: split.hold_quantity_lb,
            split_unapplied_quantity_lb: split.unapplied_quantity_lb,
            split_applied_quantity_lb: split.applied_quantity_lb,
            split_shrink_quantity_lb: split.shrink_quantity_lb,
            split_applied_quantity: split.applied_quantity,
            split_shrink_quantity: split.shrink_quantity,
          });
        }
      }
    }
  }

  return rows;
}

// ── Query param builder ────────────────────────────────────────────

function buildQueryParams(filters: AgtechTicketsFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.customer_ids) params.append("customer_ids", filters.customer_ids);
  if (filters.scale_ticket_id) params.append("scale_ticket_id", filters.scale_ticket_id);
  if (filters.ship_date) params.append("ship_date", filters.ship_date);
  if (filters.plc_id) params.append("plc_id", filters.plc_id);
  if (filters.commodity_id) params.append("commodity_id", filters.commodity_id);
  if (filters.scale_ticket_inbound_outbound) params.append("scale_ticket_inbound_outbound", filters.scale_ticket_inbound_outbound);
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

// ── React Query hook ───────────────────────────────────────────────

export function useListAgtechTickets(filters: AgtechTicketsFilters) {
  const queryParams = buildQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsResponse>("agtech/tickets", queryParams),
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
  });
}
